import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { getBasicSettings, createBasicSettings, updateBasicSettings } from '../../../services/admin/basicSettingsApi';
import { BasicSettings, BasicSettingsRequest } from '../../../types/admin';
import {
  AdminPage,
  AdminPageHeader,
  AdminPageTitle,
  AdminSection,
} from '../common/AdminPrimitives';

const ImageUpload: React.FC<{
  variant: 'banner' | 'square';
  initialImageUrl?: string;
  onFileChange?: (file: File | null) => void;
}> = ({ variant, initialImageUrl, onFileChange }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(initialImageUrl || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (initialImageUrl) {
      setImageUrl(initialImageUrl);
    }
  }, [initialImageUrl]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      const url = URL.createObjectURL(file);
      setImageUrl(url);
      onFileChange?.(file);
      return;
    }

    setImageUrl(initialImageUrl || null);
    onFileChange?.(null);
  };

  return (
    <UploadSurface $variant={variant} onClick={() => fileInputRef.current?.click()}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      <UploadContent>
        {imageUrl ? (
          <UploadedImage src={imageUrl} alt="Uploaded asset" />
        ) : (
          <UploadIcon aria-hidden="true">
            <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.9994 4.13721C16.6622 4.13721 17.1994 4.67447 17.1994 5.33721V14.8039H26.6661C27.3288 14.8039 27.8661 15.3411 27.8661 16.0039C27.8661 16.6666 27.3288 17.2039 26.6661 17.2039H17.1994V26.6705C17.1994 27.3333 16.6622 27.8705 15.9994 27.8705C15.3367 27.8705 14.7994 27.3333 14.7994 26.6705V17.2039H5.33281C4.67007 17.2039 4.13281 16.6666 4.13281 16.0039C4.13281 15.3411 4.67007 14.8039 5.33281 14.8039H14.7994V5.33721C14.7994 4.67447 15.3367 4.13721 15.9994 4.13721Z" fill="#448181" />
            </svg>
          </UploadIcon>
        )}
      </UploadContent>
    </UploadSurface>
  );
};

const HomeSettingComponent: React.FC = () => {
  const [settings, setSettings] = useState<BasicSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<BasicSettingsRequest>({
    webName: '',
  });
  const [files, setFiles] = useState<{
    favicon?: File | null;
    headerLogo?: File | null;
    adminProfileImage?: File | null;
  }>({});

  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        const data = await getBasicSettings();
        setSettings(data);
        setFormData({ webName: data.web_name });
      } catch {
        setSettings(null);
        setFormData({ webName: '' });
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const handleFileChange = (type: 'favicon' | 'headerLogo' | 'adminProfileImage') => (file: File | null) => {
    setFiles((prev) => ({
      ...prev,
      [type]: file,
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const requestData: BasicSettingsRequest = {
        webName: formData.webName,
        favicon: files.favicon || undefined,
        headerLogo: files.headerLogo || undefined,
        adminProfileImage: files.adminProfileImage || undefined,
      };

      const result = settings?.id
        ? await updateBasicSettings(settings.id, requestData)
        : await createBasicSettings(requestData);

      setSettings(result);
      setFiles({});
      alert('설정이 저장되었습니다.');
    } catch (err: any) {
      alert(`저장에 실패했습니다: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminPage>
        <AdminPageHeader>
          <AdminPageTitle>기본 설정</AdminPageTitle>
        </AdminPageHeader>
        <LoadingState>로딩 중...</LoadingState>
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <AdminPageHeader>
        <AdminPageTitle>기본 설정</AdminPageTitle>
      </AdminPageHeader>

      <AdminSection>
        <SettingsSection>
          <SplitGrid>
            <FieldGroup>
              <FieldLabel>헤더 로고</FieldLabel>
              <ImageUpload
                variant="banner"
                initialImageUrl={settings?.header_logo_url}
                onFileChange={handleFileChange('headerLogo')}
              />
            </FieldGroup>

            <FieldGroup>
              <FieldLabel>파비콘</FieldLabel>
              <ImageUpload
                variant="square"
                initialImageUrl={settings?.favicon_url}
                onFileChange={handleFileChange('favicon')}
              />
            </FieldGroup>
          </SplitGrid>
        </SettingsSection>

        <SettingsSection>
          <FieldGroup>
            <FieldLabel>홈페이지 제목 태그</FieldLabel>
            <TextInput
              type="text"
              placeholder="제목을 입력해주세요.."
              value={formData.webName || ''}
              onChange={(event) =>
                setFormData((prev) => ({
                  ...prev,
                  webName: event.target.value,
                }))
              }
            />
          </FieldGroup>
        </SettingsSection>

        <SettingsSection>
          <FieldGroup>
            <FieldLabel>관리자 프로필 이미지</FieldLabel>
            <ImageUpload
              variant="square"
              initialImageUrl={settings?.admin_profile_image_url}
              onFileChange={handleFileChange('adminProfileImage')}
            />
          </FieldGroup>
        </SettingsSection>

        <SaveRow>
          <SaveButton type="button" onClick={handleSave} disabled={saving}>
            {saving ? '저장 중...' : '저장하기'}
          </SaveButton>
        </SaveRow>
      </AdminSection>
    </AdminPage>
  );
};

const LoadingState = styled.div`
  display: flex;
  min-height: 160px;
  align-items: center;
  justify-content: center;
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
`;

const SettingsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SplitGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 200px;
  gap: 24px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldLabel = styled.h3`
  margin: 0;
  color: rgba(46, 47, 51, 0.88);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.43;
`;

const UploadSurface = styled.div<{ $variant: 'banner' | 'square' }>`
  position: relative;
  display: flex;
  overflow: hidden;
  border: 1px solid rgba(112, 115, 124, 0.16);
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: #448181;
  }

  ${({ $variant }) =>
    $variant === 'banner'
      ? css`
          width: 336px;
          height: 116px;
        `
      : css`
          width: 116px;
          height: 116px;
        `}
`;

const UploadContent = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const UploadIcon = styled.div`
  display: flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
`;

const UploadedImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
`;

const TextInput = styled.input`
  width: 100%;
  border: 1px solid rgba(112, 115, 124, 0.16);
  border-radius: 12px;
  background: #fff;
  padding: 12px;
  box-sizing: border-box;
  color: rgba(55, 56, 60, 0.88);
  font-size: 16px;
  line-height: 1.5;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: #448181;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03), 0 0 0 3px rgba(68, 129, 129, 0.1);
  }

  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }
`;

const SaveRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const SaveButton = styled.button`
  min-width: 160px;
  border-radius: 12px;
  background: #448181;
  padding: 12px 28px;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
  transition: background-color 0.2s ease, opacity 0.2s ease;

  &:hover:not(:disabled) {
    background: #3a6f6f;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default HomeSettingComponent;
